import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communityService } from '../../services/api';
import {
  emptySchedule,
  hhmmToMinutes,
  minutesToHHMM,
  totalRanges,
  validateSchedule,
  DAYS_PER_WEEK,
  MAX_RANGES_PER_DAY,
  DAY_LABELS_LONG_ES,
} from '../../utils/schedule';

/**
 * Modal full-screen para que un miembro declare sus horarios semanales.
 * Layout: 7 días apilados (uno por día), cada uno con un botón "Agregar
 * rango" hasta 3 rangos. Cada rango se muestra como una fila con
 *   <input type="time"> – <input type="time">  + dropdown opcional de sedes.
 *
 * El save manda el schedule completo al PUT que valida + compacta server-side.
 *
 * Si scheduleEnabled = false en la comunidad, el modal muestra un mensaje
 * y desactiva los inputs (el server bloquearía el PUT igual).
 */
function MyScheduleEditor({ open, communityId, communityName, onClose, onToast }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const queryKey = ['my-schedule-context', String(communityId)];
  const { data: ctx, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await communityService.getMyScheduleContext(communityId)).data,
    enabled: open && !!communityId,
    staleTime: 0,
  });

  // Estado local del editor — copia editable del schedule recibido.
  const [schedule, setSchedule] = useState(emptySchedule());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (ctx?.schedule?.days) {
      setSchedule({ days: ctx.schedule.days.map((d) => (d || []).map((r) => ({ ...r }))) });
    } else {
      setSchedule(emptySchedule());
    }
    setDirty(false);
  }, [open, ctx]);

  const sedes = ctx?.sedes || [];
  const enabled = ctx?.scheduleEnabled === true;

  const saveMut = useMutation({
    mutationFn: () => {
      // Pre-check: rangos donde sede sigue siendo `undefined` significan que
      // el usuario aún no eligió en el dropdown (placeholder "Elegir..."). No
      // dejamos guardar hasta que sea consciente la decisión.
      for (let d = 0; d < (schedule?.days?.length || 0); d++) {
        const day = schedule.days[d] || [];
        for (const r of day) {
          if (r && r.sede === undefined) {
            throw Object.assign(new Error('Validation failed'), {
              localErrors: [{ day: d, code: 'SEDE_NOT_CHOSEN' }],
            });
          }
        }
      }
      const { schedule: clean, errors } = validateSchedule(schedule, sedes);
      if (errors.length) {
        throw Object.assign(new Error('Validation failed'), { localErrors: errors });
      }
      return communityService.updateMySchedule(communityId, clean);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ['user-schedules-shared'] });
      setDirty(false);
      onToast?.('success', t('Horarios guardados'));
      onClose?.();
    },
    onError: (err) => {
      if (err?.localErrors?.length) {
        const first = err.localErrors[0];
        const day = DAY_LABELS_LONG_ES[first.day] || `Día ${first.day}`;
        const map = {
          INVALID_TIME: t('horario inválido'),
          START_NOT_LESS_THAN_END: t('hora de inicio debe ser menor a la de fin'),
          GRANULARITY: t('los horarios deben ser múltiplos de 5 minutos'),
          UNKNOWN_SEDE: t('sede no existe'),
          TOO_MANY_RANGES: t('máximo {{n}} rangos por día', { n: MAX_RANGES_PER_DAY }),
          SEDE_NOT_CHOSEN: t('elegí la sede del rango (o "sin sede" explícitamente)'),
        };
        onToast?.('error', `${day}: ${map[first.code] || first.code}`);
        return;
      }
      onToast?.('error', err?.response?.data?.message || t('Error al guardar los horarios'));
    },
  });

  const updateDay = (dayIdx, patcher) => {
    setSchedule((prev) => {
      const next = { days: prev.days.map((d, i) => (i === dayIdx ? patcher(d.slice()) : d)) };
      return next;
    });
    setDirty(true);
  };

  const addRange = (dayIdx) => updateDay(dayIdx, (day) => {
    if (day.length >= MAX_RANGES_PER_DAY) return day;
    // Si la comunidad no tiene sedes definidas, no hay nada para elegir →
    // asignamos sede: null directamente (sin sede). Sino, dejamos sede
    // undefined para forzar elección consciente en el dropdown.
    const newRange = sedes.length === 0
      ? { start: 540, end: 720, sede: null }
      : { start: 540, end: 720 };
    day.push(newRange);
    return day;
  });

  const removeRange = (dayIdx, rangeIdx) => updateDay(dayIdx, (day) => {
    day.splice(rangeIdx, 1);
    return day;
  });

  const setRangeField = (dayIdx, rangeIdx, field, value) => updateDay(dayIdx, (day) => {
    const r = { ...day[rangeIdx] };
    if (field === 'start' || field === 'end') {
      r[field] = hhmmToMinutes(value);
    } else if (field === 'sede') {
      // value === '' => placeholder "Elegir..." (no debería llegar acá porque está disabled).
      // value === '__none__' => el usuario eligió "(sin sede)" → null.
      // Cualquier otro string => nombre de sede.
      if (value === '' || value === undefined) return day; // ignorar, no cambiar nada
      r.sede = value === '__none__' ? null : value;
    }
    day[rangeIdx] = r;
    return day;
  });

  const clearAll = () => { setSchedule(emptySchedule()); setDirty(true); };

  if (!open) return null;

  // Modal más compacto si la comunidad no tiene sedes — alcanzan ~480px
  // de ancho para mostrar 7 días × ≤3 rangos en línea horizontal.
  const noSedes = enabled && sedes.length === 0;
  const modalWidth = noSedes ? 'tablet-sm:max-w-lg' : 'tablet-sm:max-w-2xl';

  return (
    <div className="fixed inset-0 z-50 flex items-end tablet-sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !saveMut.isPending && onClose?.()} />
      <div className={`relative bg-surface-container-lowest rounded-t-2xl tablet-sm:rounded-2xl w-full ${modalWidth} max-h-[92vh] flex flex-col shadow-2xl`}>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 tablet-sm:px-5 py-4 border-b border-outline-variant/15">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-on-surface">{t('Mis horarios')}</h3>
            <p className="text-xs text-on-surface-variant mt-0.5 truncate">
              {communityName ? t('en {{name}}', { name: communityName }) : t('en esta comunidad')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose?.()}
            className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center text-on-surface-variant transition-colors shrink-0"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 tablet-sm:px-5 py-4">
          {isLoading ? (
            <div className="py-12 text-center text-on-surface-variant text-sm">{t('Cargando...')}</div>
          ) : !enabled ? (
            <div className="py-10 text-center">
              <span className="material-symbols-outlined text-[48px] text-on-surface-variant/20">schedule</span>
              <p className="text-sm text-on-surface-variant mt-3">{t('Esta comunidad no tiene horarios habilitados.')}</p>
              <p className="text-xs text-on-surface-variant/70 mt-1">{t('Pedile a un admin que active la opción.')}</p>
            </div>
          ) : noSedes ? (
            // ── Layout COMPACTO (sin sedes) — fila por día con rangos en línea ──
            <div className="space-y-1">
              <p className="text-xs text-on-surface-variant mb-3">
                {t('Hasta {{n}} rangos por día.', { n: MAX_RANGES_PER_DAY })}
              </p>
              {Array.from({ length: DAYS_PER_WEEK }).map((_, d) => {
                const day = schedule.days[d] || [];
                return (
                  <div key={d} className="flex items-center gap-3 py-1.5 border-b border-outline-variant/10 last:border-b-0">
                    <span className="w-14 shrink-0 text-xs font-bold text-on-surface">
                      {DAY_LABELS_LONG_ES[d]}
                    </span>
                    <div className="flex-1 flex flex-wrap items-center gap-2">
                      {day.length === 0 && (
                        <span className="text-[11px] text-on-surface-variant/50 italic">—</span>
                      )}
                      {day.map((r, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 bg-surface-container-high/40 rounded-md pl-1.5 pr-0.5 py-0.5"
                        >
                          <input
                            type="time"
                            step="300"
                            value={minutesToHHMM(r.start)}
                            onChange={(e) => setRangeField(d, idx, 'start', e.target.value)}
                            className="w-[68px] px-1 py-0.5 rounded bg-transparent text-on-surface text-xs border-0 focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                          <span className="text-on-surface-variant text-[10px]">–</span>
                          <input
                            type="time"
                            step="300"
                            value={minutesToHHMM(r.end)}
                            onChange={(e) => setRangeField(d, idx, 'end', e.target.value)}
                            className="w-[68px] px-1 py-0.5 rounded bg-transparent text-on-surface text-xs border-0 focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                          <button
                            type="button"
                            onClick={() => removeRange(d, idx)}
                            className="w-5 h-5 rounded text-on-surface-variant/60 hover:text-error hover:bg-error/10 flex items-center justify-center"
                            aria-label={t('Eliminar rango')}
                          >
                            <span className="material-symbols-outlined text-[12px]">close</span>
                          </button>
                        </span>
                      ))}
                      {day.length < MAX_RANGES_PER_DAY && (
                        <button
                          type="button"
                          onClick={() => addRange(d)}
                          className="inline-flex items-center gap-0.5 px-1.5 py-1 rounded-md text-primary hover:bg-primary/10 text-[11px] font-bold transition-colors"
                          aria-label={t('Agregar rango')}
                          title={t('Agregar rango')}
                        >
                          <span className="material-symbols-outlined text-[14px]">add</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="pt-3">
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-on-surface-variant hover:text-error font-medium underline-offset-2 hover:underline transition-colors"
                >
                  {t('Vaciar todos los horarios')}
                </button>
              </div>
            </div>
          ) : (
            // ── Layout ESTÁNDAR (con sedes) — card por día con rangos verticales ──
            <div className="space-y-3">
              <p className="text-xs text-on-surface-variant">
                {t('Agregá hasta {{n}} rangos por día. Si querés podés especificar la sede.', { n: MAX_RANGES_PER_DAY })}
              </p>
              {Array.from({ length: DAYS_PER_WEEK }).map((_, d) => {
                const day = schedule.days[d] || [];
                return (
                  <div key={d} className="rounded-xl border border-outline-variant/15 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-on-surface">{DAY_LABELS_LONG_ES[d]}</p>
                      {day.length < MAX_RANGES_PER_DAY && (
                        <button
                          type="button"
                          onClick={() => addRange(d)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <span className="material-symbols-outlined text-[14px]">add</span>
                          {t('Agregar rango')}
                        </button>
                      )}
                    </div>
                    {day.length === 0 ? (
                      <p className="text-xs text-on-surface-variant/60 italic">{t('Sin horarios.')}</p>
                    ) : (
                      <div className="space-y-2">
                        {day.map((r, idx) => (
                          <div key={idx} className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2 shrink-0">
                              <input
                                type="time"
                                step="300"
                                value={minutesToHHMM(r.start)}
                                onChange={(e) => setRangeField(d, idx, 'start', e.target.value)}
                                className="px-2 py-1 rounded-md border border-outline-variant/30 bg-transparent text-on-surface text-xs"
                              />
                              <span className="text-on-surface-variant text-xs">–</span>
                              <input
                                type="time"
                                step="300"
                                value={minutesToHHMM(r.end)}
                                onChange={(e) => setRangeField(d, idx, 'end', e.target.value)}
                                className="px-2 py-1 rounded-md border border-outline-variant/30 bg-transparent text-on-surface text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0 basis-full tablet-sm:basis-0">
                              {(() => {
                                const selectVal = r.sede === undefined
                                  ? ''
                                  : (r.sede === null ? '__none__' : r.sede);
                                const needsChoice = r.sede === undefined;
                                return (
                                  <select
                                    value={selectVal}
                                    onChange={(e) => setRangeField(d, idx, 'sede', e.target.value)}
                                    className={`flex-1 min-w-0 px-2 py-1 rounded-md border bg-transparent text-on-surface text-xs ${
                                      needsChoice ? 'border-error/40 text-error' : 'border-outline-variant/30'
                                    }`}
                                  >
                                    <option value="" disabled>{t('Elegir...')}</option>
                                    <option value="__none__">{t('(sin sede)')}</option>
                                    {sedes.map((s) => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                );
                              })()}
                              <button
                                type="button"
                                onClick={() => removeRange(d, idx)}
                                className="w-7 h-7 shrink-0 rounded text-on-surface-variant hover:text-error hover:bg-error/10 flex items-center justify-center"
                                aria-label={t('Eliminar rango')}
                              >
                                <span className="material-symbols-outlined text-[16px]">delete</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="pt-2">
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-on-surface-variant hover:text-error font-medium underline-offset-2 hover:underline transition-colors"
                >
                  {t('Vaciar todos los horarios')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 tablet-sm:px-5 py-3 border-t border-outline-variant/15 bg-surface-container/50 rounded-b-2xl">
          <p className="text-xs text-on-surface-variant">
            {totalRanges(schedule)} {t('rango(s)')}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => !saveMut.isPending && onClose?.()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors"
            >
              {t('Cancelar')}
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={!dirty || saveMut.isPending || !enabled}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saveMut.isPending ? t('Guardando...') : t('Guardar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyScheduleEditor;
